import { Link } from "zudoku/components";
import { ChevronDown } from "zudoku/icons";
import { useLocation } from "zudoku/router";
import { isUnder } from "./reference/define";
import { cartWidget } from "./reference/cart-widget/sections";
import { subblySdk } from "./reference/subbly-sdk/sections";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "zudoku/ui/DropdownMenu.js";

type MenuLink = { label: string; to: string; description?: string };
type MenuSection = { label?: string; items: MenuLink[] };

const isExternal = (to: string) => /^https?:\/\//.test(to);

const NavDropdown = ({
  label,
  sections,
}: {
  label: string;
  sections: MenuSection[];
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger className="my-2 flex cursor-pointer items-center gap-1 rounded-md px-2.5 py-1.5 font-medium text-muted-foreground hover:bg-accent hover:text-foreground data-[state=open]:bg-accent data-[state=open]:text-foreground">
      {label}
      <ChevronDown size={14} />
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="min-w-56">
      {sections.map((section, i) => (
        <div key={section.label ?? i}>
          {i > 0 && <DropdownMenuSeparator />}
          {section.label && (
            <DropdownMenuLabel className="text-xs uppercase tracking-wide text-muted-foreground">
              {section.label}
            </DropdownMenuLabel>
          )}
          {section.items.map((item) => (
            <DropdownMenuItem
              key={item.to}
              asChild
              className="cursor-pointer hover:bg-accent hover:text-accent-foreground"
            >
              {isExternal(item.to) ? (
                <a href={item.to} target="_blank" rel="noopener">
                  {item.label}
                </a>
              ) : (
                <Link to={item.to}>{item.label}</Link>
              )}
            </DropdownMenuItem>
          ))}
        </div>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

// `unlisted` keeps a reference out of the menu; its pages still get the
// reference header. Shared by link only.
const REFERENCES = [
  { label: "Storefront API", to: "/api/storefront" },
  { label: "Private API", to: "/api/private" },
  { label: "Orders API", to: "/api/orders", unlisted: true },
  { label: "Subbly.js SDK", to: subblySdk.path },
  { label: "SubblyCart.js widget", to: cartWidget.path },
];

const listed = (to: string) =>
  REFERENCES.filter((r) => !r.unlisted && r.to.startsWith(to));

const ReferencesMenu = () => (
  <NavDropdown
    label="References"
    sections={[
      { label: "API reference", items: listed("/api/") },
      { label: "SDKs & widgets", items: listed("/reference/") },
    ]}
  />
);

const HelpMenu = () => (
  <NavDropdown
    label="Help"
    sections={[
      {
        items: [
          { label: "Support", to: "https://support.subbly.co" },
          { label: "Main site", to: "https://www.subbly.co/" },
        ],
      },
    ]}
  />
);

/*
  The row under the header, like Stripe's. On an article page the tabs sit on
  the left and this renders the "References" and "Help" menus on the right. A
  reference page is its own area, as Stripe's API and JS references are: the
  tabs are hidden (see styles.css, keyed on `data-shell`), the name of the
  reference takes their place, and a "Docs" link leads back to the articles.
*/
export const TopNavMenus = () => {
  const { pathname } = useLocation();
  const reference = REFERENCES.find(({ to }) => isUnder(pathname, to));

  if (reference) {
    return (
      <div
        data-shell="reference"
        className="flex flex-1 items-center justify-between text-sm"
      >
        <span className="font-medium">{reference.label} reference</span>
        <div className="flex items-center gap-6">
          <Link
            to="/get-started/overview"
            className="py-3.5 font-medium text-muted-foreground hover:text-foreground"
          >
            Docs
          </Link>
          <ReferencesMenu />
          <HelpMenu />
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 text-sm">
      <ReferencesMenu />
      <HelpMenu />
    </div>
  );
};

// Header right side: account links, like the marketing site.
export const HeaderLinks = () => (
  <div className="mr-3 hidden items-center gap-5 text-sm md:flex">
    <a
      href="https://www.subbly.co/admin/auth/login"
      className="font-medium text-muted-foreground hover:text-foreground"
    >
      Log in
    </a>
    <a
      href="https://www.subbly.co/admin/auth/register"
      className="btn-subbly btn-subbly-primary px-4 py-1.5 text-sm"
    >
      Get started
    </a>
  </div>
);
